import React, { useEffect, useState } from "react";
import {
  Container,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useFormContext } from "react-hook-form";
import Iconify from "components/iconify";
import customFetch from "hooks/fetchWrapper";

export default function FilterSection() {
  const { setValue, watch } = useFormContext();
  const selectedEmirate = watch("emirate");
  const selectedArea = watch("area");

  const [emirates, setEmirates] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loadingEmirates, setLoadingEmirates] = useState(true);
  const [loadingAreas, setLoadingAreas] = useState(false);

  // Load Emirates
  useEffect(() => {
    const loadEmirates = async () => {
      try {
        setLoadingEmirates(true);
        const data = await customFetch("emirates");
        setEmirates(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        console.error("Failed to load emirates:", err);
        setEmirates([]);
      } finally {
        setLoadingEmirates(false);
      }
    };
    loadEmirates();
  }, []);

  // Load Areas when Emirate selected
  useEffect(() => {
    if (!selectedEmirate) {
      setAreas([]);
      setValue("area", "");
      return;
    }

    const loadAreas = async () => {
      try {
        setLoadingAreas(true);
        const res = await customFetch(`areas/emirate/${selectedEmirate}`);
        setAreas(res.status && res.data ? res.data : []);
      } catch (err) {
        console.error("Failed to load areas:", err);
        setAreas([]);
      } finally {
        setLoadingAreas(false);
      }
    };
    loadAreas();
  }, [selectedEmirate, setValue]);

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        justifyContent="center"
        alignItems="stretch"
      >
        {/* Emirate Dropdown */}
        <FormControl
          fullWidth
          sx={{
            maxWidth: 350,
            "& .MuiOutlinedInput-root": {
              borderRadius: 50,
              bgcolor: "white",
              fontSize: "1.1rem",
              "& fieldset": {
                borderWidth: 2,
                borderColor: "#4caf50",
              },
              "&:hover fieldset": {
                borderColor: "#388e3c",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#4caf50",
                borderWidth: 2,
              },
            },
            "& .MuiInputLabel-root": {
              fontSize: "1.1rem",
              fontWeight: 500,
              "&.Mui-focused": {
                color: "#4caf50",
              },
            },
          }}
        >
          <InputLabel id="emirate-label">Emirate</InputLabel>
          <Select
            labelId="emirate-label"
            value={selectedEmirate}
            label="Emirate"
            onChange={(e) => {
              setValue("emirate", e.target.value);
              setValue("area", "");
              setValue("routeSearch", "");
            }}
            disabled={loadingEmirates}
            sx={{ py: 1 }}
          >
            <MenuItem value="" disabled>
              Select Emirate
            </MenuItem>
            {emirates.map((e) => (
              <MenuItem key={e.eid} value={e.eid}>
                {e.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Area Dropdown */}
        <FormControl
          fullWidth
          sx={{
            maxWidth: 350,
            "& .MuiOutlinedInput-root": {
              borderRadius: 50,
              bgcolor: "white",
              fontSize: "1.1rem",
              "& fieldset": {
                borderWidth: 2,
                borderColor: "#4caf50",
              },
              "&:hover fieldset": {
                borderColor: "#388e3c",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#4caf50",
                borderWidth: 2,
              },
            },
            "& .MuiInputLabel-root": {
              fontSize: "1.1rem",
              fontWeight: 500,
              "&.Mui-focused": {
                color: "#4caf50",
              },
            },
          }}
        >
          <InputLabel id="area-label">Area</InputLabel>
          <Select
            labelId="area-label"
            value={selectedArea}
            label="Area"
            onChange={(e) => {
              setValue("area", e.target.value);
              setValue("routeSearch", "");
            }}
            disabled={!selectedEmirate || loadingAreas}
            sx={{ py: 1 }}
          >
            <MenuItem value="" disabled>
              Select Area
            </MenuItem>
            {areas.map((a) => (
              <MenuItem key={a.aid} value={a.aid}>
                {a.code ? `${a.code} - ${a.areaname}` : a.areaname} ({a.emirateName})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Route Search Input */}
        <TextField
          fullWidth
          label="Search Bustop"
          placeholder="Search by bustop name..."
          value={watch("routeSearch") || ""}
          onChange={(e) => setValue("routeSearch", e.target.value)}
          disabled={!selectedArea}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="mdi:magnify" sx={{ color: "#4caf50", fontSize: 24 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 350,
            "& .MuiOutlinedInput-root": {
              borderRadius: 50,
              bgcolor: "white",
              fontSize: "1.1rem",
              py: 0.5,
              "& fieldset": {
                borderWidth: 2,
                borderColor: "#4caf50",
              },
              "&:hover fieldset": {
                borderColor: "#388e3c",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#4caf50",
                borderWidth: 2,
              },
            },
            "& .MuiInputLabel-root": {
              fontSize: "1.1rem",
              fontWeight: 500,
              "&.Mui-focused": {
                color: "#4caf50",
              },
            },
          }}
        />
      </Stack>

      {(loadingEmirates || loadingAreas) && (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress sx={{ color: "#4caf50" }} />
        </Box>
      )}
    </Container>
  );
}
